import React from 'react';
import { css } from '@tanium/react-emotion-9';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@tanium/react-theme-context';
import { CircleIcon } from '@tanium/react-composed-icons';
import { TrashIcon } from '@tanium/react-graphics';
import { Box } from '@tanium/react-box';
import { HEX_1D2939, HEX_F2F6FA, HEX_212F41, HEX_253548, HEX_335880 } from '@tanium/colors';
import { Theme } from '@tanium/theme-data';
import { defineColor } from '@tanium/react-theme-definition';
import { useSelector } from 'react-redux';
import MediumBoldText from 'components/styles/text/MediumBoldText';
import MediumText from 'components/styles/text/MediumText';
import { RequestAccessInput } from 'gql/Request';
import { HostTargetAccess } from 'gql/HostTargetAccess';
import AwsRoleTargetAccessSelectors from 'components/pages/console/store/AwsRoleTargetAccessSelectors';
import HostTargetAccessSelectors from 'components/pages/console/store/HostTargetAccessSelectors';
import { AwsRoleTargetAccess } from 'gql/AwsRoleTargetAccess';
import { BastionTargetAccess } from 'gql/BastionTargetAccess';
import BastionTargetAccessSelectors from 'components/pages/console/store/BastionTargetAccessSelectors';
import { K8sRoleTargetAccess } from 'gql/K8sRoleTargetAccess';
import K8sRoleTargetAccessSelectors from 'components/pages/console/store/K8sRoleTargetAccessSelectors';
import APIAccessType from 'common/IAPIAccessType';
import { MsEntraIDGroupTargetAccess } from 'gql/MsEntraIDGroupTargetAccess';
import MsEntraIDGroupTargetAccessSelectors from 'components/pages/console/store/MsEntraIDGroupTargetAccessSelectors';

const BorderColor = defineColor([HEX_1D2939, HEX_F2F6FA]);
const BodyFill = defineColor(['transparent', HEX_253548]);
const BodyHover = defineColor(['tranparent', HEX_212F41]);

const BodyClass = (theme: Theme) => css`
  &:hover {
    background-color: ${BodyHover(theme)};
  }
`;
export interface ISelectedAccessTypeCard {
  id: number;
  onDeleteClicked: (id: number) => void;
}

export const SelectedAccessTypeCard = ({
  id,
  type,
  targetAccessID,
  onDeleteClicked,
}: ISelectedAccessTypeCard & RequestAccessInput): React.ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();
  const msEntraIDGroupTargetAccess: MsEntraIDGroupTargetAccess | undefined = useSelector(
    MsEntraIDGroupTargetAccessSelectors.selectMsEntraIDGroupTargetAccess(targetAccessID)
  );
  const awsRoleTargetAccess: AwsRoleTargetAccess | undefined = useSelector(
    AwsRoleTargetAccessSelectors.selectAwsRoleTargetAccess(targetAccessID)
  );
  const hostTargetAccess: HostTargetAccess | undefined = useSelector(
    HostTargetAccessSelectors.selectHostTargetAccess(targetAccessID)
  );
  const bastionTargetAccess: BastionTargetAccess | undefined = useSelector(
    BastionTargetAccessSelectors.selectBastionTargetAccess(targetAccessID)
  );
  const kubernetesRoleTargetAccess: K8sRoleTargetAccess | undefined = useSelector(
    K8sRoleTargetAccessSelectors.selectK8sRoleTargetAccess(targetAccessID)
  );

  const deleteClicked = () => {
    onDeleteClicked(id);
  };

  return (
    <Box
      borderTop={1}
      borderLeft={1}
      borderRight={1}
      borderColor={BorderColor}
      borderTopRadius={!id ? '0.2em' : 0}
      display="flex"
      justifyContent="space-between"
      background={BodyFill}
      className={BodyClass(theme)}
      data-testid="selected-access-type-card"
    >
      <Box>
        <Box display="inline-block" p={2}>
          <Box display="inline-block" color={theme.colors.subtle} pr={1}>
            <MediumText>{t('ACCESS_TYPE')}:</MediumText>
          </Box>
          <Box display="inline-block">
            <MediumBoldText>{t(type)}</MediumBoldText>
          </Box>
        </Box>
        {msEntraIDGroupTargetAccess && (
          <Box display="inline-block" p={2}>
            <Box display="inline-block" color={theme.colors.subtle} pr={1}>
              <MediumText>{t('ROLE')}:</MediumText>
            </Box>
            <MediumBoldText>{msEntraIDGroupTargetAccess.roleName}</MediumBoldText>
          </Box>
        )}
        {awsRoleTargetAccess && (
          <Box display="inline-block" p={2}>
            <Box display="inline-block" color={theme.colors.subtle} pr={1}>
              <MediumText>{t('ROLE')}:</MediumText>
            </Box>
            <MediumBoldText>{awsRoleTargetAccess.awsRoleName}</MediumBoldText>
          </Box>
        )}
        {hostTargetAccess && (
          <Box display="inline-block" p={2}>
            <Box display="inline-block" color={theme.colors.subtle} pr={1}>
              <MediumText>{t('HOST')}:</MediumText>
            </Box>
            <MediumBoldText>
              {hostTargetAccess.userName}@{hostTargetAccess.hostName}
            </MediumBoldText>
          </Box>
        )}
        {bastionTargetAccess && (
          <Box display="inline-block" p={2}>
            <Box display="inline-block" color={theme.colors.subtle} pr={1}>
              <MediumText>{t('BASTION_TYPE')}:</MediumText>
            </Box>
            <MediumBoldText>{bastionTargetAccess.name}</MediumBoldText>
          </Box>
        )}
        {kubernetesRoleTargetAccess && (
          <Box display="inline-block" p={2}>
            <Box display="inline-block" color={theme.colors.subtle} pr={1}>
              <MediumText>{t('KUBERNETES_ROLE')}:</MediumText>
            </Box>
            <MediumBoldText>
              {kubernetesRoleTargetAccess.apiAccessType === APIAccessType.ROLE
                ? `${kubernetesRoleTargetAccess.apiAccessType}:${kubernetesRoleTargetAccess.roleName}:${kubernetesRoleTargetAccess.namespace}`
                : `${kubernetesRoleTargetAccess.apiAccessType}:${kubernetesRoleTargetAccess.roleName}`}
            </MediumBoldText>
          </Box>
        )}
      </Box>

      <Box p={2} style={{ cursor: 'pointer' }} onClick={deleteClicked} data-testid="delete-icon">
        <CircleIcon
          icon={TrashIcon}
          outerRadius={5}
          innerRadius={7}
          iconColor={HEX_335880}
          backgroundColor={'rgba(0,0,0,0)'}
        />
      </Box>
    </Box>
  );
};

export default SelectedAccessTypeCard;
